/* eslint-disable @typescript-eslint/no-misused-promises */
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  SmartForm,
  SmartSubmit,
  SmartTextField,
  usePopup,
} from "@nodeon/react-components";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  GeopickingEditDto,
  GeopickingTypeEnum,
  useGeopickingApi,
} from "~/api/GeneratedApi";
import {
  setActiveCesiumTool,
  setCesiumPopupPosition,
  setSelectedCesiumItem,
} from "~/redux/reducers/mapReducer";
import { useAppDispatch } from "~/redux/store/store";
import { useAccess } from "~/utils/hooks/useAccess";
import useAuth from "~/utils/hooks/useAuth";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";
import { GenericPopup } from "../../popups/GenericPopup";
import { CesiumGeopickingItem } from "../types/CesiumMapItems";

type Props = ClickProps | HoverProps;

type ClickProps = {
  clickData?: CesiumGeopickingItem;
  hoverData?: CesiumGeopickingItem;
};

type HoverProps = {
  hoverData?: CesiumGeopickingItem;
  clickData?: CesiumGeopickingItem;
};

type Form = {
  content: string;
};

export const CesiumGeopickingPopup = ({
  clickData,
  hoverData,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("cesium");
  const dispatch = useAppDispatch();
  const dateFormatter = useDateFormatter();
  const { addGeopicking, getGeopickings, deleteGeopicking, editGeopicking } =
    useGeopickingApi();
  const { confirm } = usePopup();
  const [editing, setEditing] = React.useState(false);
  const { user } = useAuth();
  const { isGlobalAdmin } = useAccess();

  const data = clickData ?? hoverData;

  const position = React.useMemo(() => {
    if (data) return data.location;
    return null;
  }, [data]);
  const seaSurveyId = data?.seaSurvey ? data.seaSurvey.id : null;

  const { mutateAsync: addAsync } = useCustomMutation(addGeopicking);
  const { mutateAsync: editAsync } = useCustomMutation(
    ({ d, id }: { d: GeopickingEditDto; id: number }) => editGeopicking(d, id),
  );
  const { mutateAsync: deleteAsync } = useCustomMutation(deleteGeopicking);
  const { refetch: refetchGeopickings } = useCustomQuery("geopickings", () =>
    getGeopickings(),
  );

  const handleSubmit = React.useCallback(
    async (d: Form) => {
      if (!position || !seaSurveyId) return;
      if (editing && !data?.id) return;
      const action =
        editing && data?.id
          ? editAsync({ d: { content: d.content ?? "" }, id: data.id })
          : addAsync({
              ...d,
              location: position,
              seaSurveyId,
              type: GeopickingTypeEnum.Comment,
            });
      await action;
      await refetchGeopickings();
      if (!editing) {
        dispatch(setCesiumPopupPosition(undefined));
        dispatch(setSelectedCesiumItem(undefined));
      }
      dispatch(setActiveCesiumTool(undefined));
      setEditing(false);
    },
    [
      addAsync,
      data?.id,
      dispatch,
      editAsync,
      editing,
      position,
      refetchGeopickings,
      seaSurveyId,
    ],
  );

  const content = data?.content ? data.content : "";

  const creating = !data?.id;

  const handleDelete = React.useCallback(async () => {
    if (!data?.id) return;
    await confirm({
      title: t("general:are-you-sure"),
      icon: <DeleteIcon />,
    });
    await deleteAsync(data.id);
    await refetchGeopickings();
    dispatch(setSelectedCesiumItem(undefined));
    dispatch(setCesiumPopupPosition(undefined));
    dispatch(setActiveCesiumTool(undefined));
  }, [confirm, data?.id, deleteAsync, dispatch, refetchGeopickings, t]);

  const canEditOrDelete = data?.creator?.id === user?.id || isGlobalAdmin;

  if (!position || !seaSurveyId) return null;
  return (
    <GenericPopup
      clicked
      avatarText="!"
      title={data?.creator?.name ? data.creator.name : t("new-geopicking")}
      subheader={
        data?.createdTimestamp
          ? dateFormatter({ date: data.createdTimestamp })
          : ""
      }
      content={
        <>
          <SmartForm<Form>
            onSubmit={handleSubmit}
            formProps={{
              defaultValues: {
                content,
              },
            }}
            gridProps={{ direction: "column" }}
          >
            <SmartTextField<Form>
              name="content"
              multiline
              disabled={!creating && !editing}
              autoFocus
              fullWidth
              label={t("geopicking-comment")}
            />
            {creating || editing ? <SmartSubmit /> : null}
          </SmartForm>
          {!!clickData && !creating && canEditOrDelete ? (
            <Box sx={{ display: "flex", mt: 1 }}>
              <Tooltip title={t("general:delete").toString()}>
                <IconButton sx={{ marginRight: "auto" }} onClick={handleDelete}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              {editing ? (
                <Tooltip title={t("general:cancel").toString()}>
                  <IconButton
                    sx={{ marginLeft: "auto" }}
                    onClick={() => setEditing(false)}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={t("general:edit").toString()}>
                  <IconButton
                    sx={{ marginLeft: "auto" }}
                    onClick={() => setEditing(true)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ) : null}
        </>
      }
    />
  );
};
